import { useEffect, useState, useRef } from "react";
import { 
  GetDataFromApiWithParams, 
  ExecutePostWithParams 
} from "../../../../services/api/ExecuteApiRequests";
import IPDCFinalTable from "../../../../components/IPDCFinalTable";
import { Box, Button, Alert, CircularProgress, Typography, Card, CardContent } from "@mui/material";
import { Check, Close, Download, TableChart, Verified } from "@mui/icons-material";

export default function TerminationApproval() {
  const [terminations, setTerminations] = useState([]);
  const componentRef = useRef();
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    fetchVerifiedTerminations();
  }, []);

  const fetchVerifiedTerminations = async () => {
    try {
      setLoading(true);
      setError("");
      console.log("Fetching verified termination requests from API...");
      
      // Get termination requests that are already verified and pending approval
      const resultRow = await GetDataFromApiWithParams("agreements/termination-requests/verified");
      console.log("Verified Termination Requests API Response:", resultRow);
        if (resultRow && Array.isArray(resultRow)) {
        console.log(`Total verified termination requests received: ${resultRow.length}`);
        
        if (resultRow.length > 0) {
          console.log("First verified termination sample:", resultRow[0]);
          setTerminations(resultRow);
          
          // Define columns to show for approval view
          const importantColumns = [
            'id', 'agreement_id', 'company_name', 'tin_no', 
            'request_date', 'verification_date', 'verified_by',
            'reason', 'description', 'status'
          ];
          
          // Use important columns that actually exist in the data
          const availableColumns = importantColumns.filter(col => 
            resultRow[0].hasOwnProperty(col)
          );
          
          console.log("Columns available for display:", availableColumns);
          setSelectedColumns([...availableColumns, 'actions']);
        } else {
          setTerminations([]);
          setError("No verified termination requests found for approval.");
        }
      } else {
        setError("No termination request data received from API.");
      }
    } catch (err) {
      console.error("Error fetching verified termination requests:", err);
      setError("Error fetching verified termination requests: " + (err.message || "Unknown error"));
    } finally {
      setLoading(false);
    }
  };

 const handleApproval = async (termination, action) => {
  try {
    const terminationId = termination.no; // Use 'no' instead of 'id'
    if (!terminationId) {
      setError("Cannot approve: Termination request ID is missing");
      return;
    }

    const approvalData = {
      approval_date: new Date().toISOString().split('T')[0],
      remarks: `Termination request ${action}d by Operation Management Approver`,
      action: action, // 'approve' or 'reject'
      approved_by: "operation_management_division_head",
    };

    console.log(`Sending ${action} for termination request:`, terminationId);
    const response = await ExecutePostWithParams(`agreements/termination-requests/${terminationId}/approve`, approvalData);
    
    if (response) {
      setTerminations(prev => prev.filter(item => item.no !== terminationId));
      setSuccess(`Termination request successfully ${action === "approve" ? "approved" : "rejected"}!`);
      setError("");
      setTimeout(() => setSuccess(""), 5000);
    } else {
      setError(`Failed to ${action} termination request.`);
      setSuccess("");
    }
  } catch (err) {
    console.error("Approval error:", err);
    setError(`Error: ${err.message || "Unknown error during approval"}`);
    setSuccess("");
  }
};

  const handleExportToExcel = (termination) => {
    try {
      // Create a CSV content from the termination data
      const headers = Object.keys(termination).filter(key => 
        key !== 'actions' && 
        typeof termination[key] !== 'object' &&
        !key.startsWith('_')
      );
      
      const csvHeaders = headers.join(',');
      const csvRow = headers.map(header => {
        const value = termination[header];
        // Handle values that might contain commas by wrapping in quotes
        return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
      }).join(',');
      
      const csvContent = `${csvHeaders}\n${csvRow}`;
      
      // Create a Blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `termination_request_${termination.id}_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported termination request ${termination.id} to Excel`);
    } catch (err) {
      console.error("Error exporting to Excel:", err);
      setError("Error exporting termination request data to Excel");
    }
  };

  const handleExportAllToExcel = () => {
    try {
      if (enhancedTerminations.length === 0) {
        setError("No data to export");
        return;
      }

      // Get all headers from the first termination (excluding actions and objects)
      const headers = Object.keys(enhancedTerminations[0]).filter(key => 
        key !== 'actions' && 
        typeof enhancedTerminations[0][key] !== 'object' &&
        !key.startsWith('_')
      );
      
      // Create CSV headers
      const csvHeaders = headers.join(',');
      
      // Create CSV rows
      const csvRows = enhancedTerminations.map(termination => {
        return headers.map(header => {
          const value = termination[header];
          // Handle values that might contain commas by wrapping in quotes
          return typeof value === 'string' && value.includes(',') ? `"${value}"` : value;
        }).join(',');
      }).join('\n');
      
      const csvContent = `${csvHeaders}\n${csvRows}`;
      
      // Create a Blob and download
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      
      link.setAttribute('href', url);
      link.setAttribute('download', `all_verified_termination_requests_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      console.log(`Exported all ${enhancedTerminations.length} termination requests to Excel`);
      setSuccess(`All ${enhancedTerminations.length} termination requests exported successfully!`);
      setTimeout(() => setSuccess(""), 5000);
    } catch (err) {
      console.error("Error exporting all to Excel:", err);
      setError("Error exporting all termination requests data to Excel");
    }
  };

  // Enhanced terminations with action buttons for approval
  const enhancedTerminations = terminations.map(termination => {
    // Try different possible ID field names
    const terminationId = termination.id || termination.agreement_id || termination.no;
    console.log("Termination ID found:", terminationId, "from termination:", termination);
    
    const status = termination.status || 'VERIFIED';
    
    return {
      ...termination,
      id: terminationId, // Ensure id is set
      status: status,
      actions: (
        <Box sx={{ 
          display: "flex", 
          gap: 1, 
          flexDirection: 'row',
          flexWrap: 'nowrap',
          justifyContent: 'center',
          alignItems: 'center'
        }}>
          <Button
            variant="outlined"
            color="primary"
            size="small"
            startIcon={<Download />}
            onClick={() => handleExportToExcel(termination)}
            disabled={!terminationId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="success"
            size="small"
            startIcon={<Verified />}
            onClick={() => handleApproval(termination, "approve")}
            disabled={!terminationId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Approve
          </Button>
          <Button
            variant="contained"
            color="error"
            size="small"
            startIcon={<Close />}
            onClick={() => handleApproval(termination, "reject")}
            disabled={!terminationId}
            sx={{ whiteSpace: 'nowrap' }}
          >
            Reject
          </Button>
        </Box>
      )
    };
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading verified termination requests for approval...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {success && (
            <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess("")}>
              {success}
            </Alert>
          )}

          {enhancedTerminations.length > 0 ? (
            <>
              <IPDCFinalTable
                ref={componentRef}
                data={enhancedTerminations}
                setData={setTerminations}
                selectedColumns={selectedColumns}
                title={`Verified Termination Requests for Approval (${enhancedTerminations.length})`}
                hasAttachment={false}
              />
              
              {/* Export All Button BELOW the table */}
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<TableChart />}
                  onClick={handleExportAllToExcel}
                  disabled={enhancedTerminations.length === 0}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Export All Data ({enhancedTerminations.length} rows)
                </Button>
              </Box>
            </>
          ) : (
            !error && (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="textSecondary">
                  No verified termination requests found for approval.
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  All verified termination requests have been processed or there are no requests waiting for approval.
                </Typography>
              </Box>
            )
          )}
        </CardContent>
      </Card>
    </Box>
  );
}